import React from "react";
import { Alert } from "antd";

import PolicyCriticalFields from "./PolicyCriticalFields";
import { controller as rootController } from "../../../../controller";
import {
  REVIEW_FIELDS,
  describeMissing,
  missingCriticalFields,
} from "../../lib/policyCompleteness";

/**
 * Persist the user's edits, writing ONLY fields whose value actually changed.
 *
 * This matters: UpdateInsurancePolicyView appends every field present in the
 * request body to the policy's `manually_edited_fields`, which permanently locks
 * it against future PMS syncs. PATCHing all three fields every time would lock
 * values that came from the PMS and are better left syncable.
 *
 * Returns true when everything saved (or there was nothing to save).
 */
export async function savePolicyDrafts(policies, drafts) {
  const edits = [];
  (policies || []).forEach(function (policy) {
    const draft = (drafts && drafts[policy.id]) || {};
    const changed = {};
    // REVIEW_FIELDS, not CRITICAL_FIELDS: relation_to_member is editable on
    // these surfaces but never required, and an edit to it has to persist like
    // any other. The changed-only rule below still applies — see above.
    REVIEW_FIELDS.forEach(function (field) {
      if (draft[field] === undefined) return;
      const next = String(draft[field] || "").trim();
      const prev = String(policy[field] || "").trim();
      if (next !== prev) changed[field] = next;
    });
    if (Object.keys(changed).length) edits.push({ id: policy.id, changed });
  });
  if (!edits.length) return true;

  const results = await Promise.all(
    edits.map(function (e) {
      return rootController
        .updatePatientInsurancePolicy(e.id, e.changed)
        .then(function (res) { return !!(res && res.status >= 200 && res.status < 300); })
        .catch(function (err) { console.error(err); return false; });
    }),
  );
  return results.every(Boolean);
}

/**
 * Review + complete the data a verification needs, for one or more policies.
 *
 * Shared by the Insurance Verification composer's Details step and the
 * Instant-AI flow (SelectPolicyItemModal step 2 / PolicyCompletionModal), so the
 * two can't drift on what is required or how it is saved.
 *
 * This is an EDIT surface, not a viewer: it holds edits in `drafts` owned by the
 * host, which persists them on submit. It deliberately does not save per-field —
 * every field written through `updatePatientInsurancePolicy` is appended to the
 * policy's `manually_edited_fields` and locked against future PMS syncs, so we
 * only want to write what the user actually touched. The host diffs before saving.
 *
 * Member DOB gets MemberDobSuggestion underneath it, which offers the birth date
 * of a same-office patient matching the member's name. It renders nothing when
 * there are no candidates, so it is safe to mount unconditionally.
 *
 * @param {Array}  policies  the policies being verified (already selected)
 * @param {Object} drafts    { [policyId]: { member_id, contract_number, date_of_birth } }
 * @param {Function} onChange (policyId, field, value) => void
 * @param {number} patientId  for the DOB suggester when a policy has no id yet
 * @param {number|null} maxHeight  caps the policy list and scrolls it internally.
 *   Right inside a modal, which must not outgrow the viewport. Pass `null` when
 *   the host is ALREADY a full-height scroller (the Insurance Requests drawer) —
 *   otherwise you get a short scroll box nested in a tall one, which is the
 *   worst of both.
 */
export default function PolicyCompletionStep({ policies, drafts, onChange, patientId, maxHeight = 340 }) {
  const list = policies || [];

  // What's still missing AFTER the user's in-progress edits.
  function effective(policy) {
    const draft = (drafts && drafts[policy.id]) || {};
    return Object.assign({}, policy, draft);
  }

  const stillMissing = list.reduce(function (acc, p) {
    return acc + missingCriticalFields(effective(p)).length;
  }, 0);

  return (
    <div>
      {stillMissing > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Some details are missing"
          description="Verifications fail at the carrier without these, so please fill them in before sending."
        />
      ) : (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message="All required details are present"
          description="Review the values below, then send the request."
        />
      )}

      <div
        style={maxHeight ? { maxHeight, overflowY: "auto", paddingRight: 4 } : undefined}
        className={maxHeight ? "custom-scroll" : undefined}
      >
        {list.map(function (policy) {
          const merged = effective(policy);
          const missing = missingCriticalFields(merged);
          const draft = (drafts && drafts[policy.id]) || {};
          return (
            <div
              key={policy.id}
              style={{
                border: "1px solid " + (missing.length ? "#ffd591" : "#e8e0f5"),
                background: missing.length ? "#fffbf5" : "#fff",
                borderRadius: 8,
                padding: 14,
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                <span style={{ fontWeight: 600, color: "#3d2f5e" }}>
                  {policy.insurance_name_display || policy.insurance_name || "Policy"}
                </span>
                <span style={{ fontSize: 12, color: "#848696" }}>{policy.insurance_type_display}</span>
              </div>

              {missing.length ? (
                <div style={{ fontSize: 12, color: "#d46b08", marginBottom: 10 }}>
                  Missing {describeMissing(missing)}.
                </div>
              ) : null}

              <PolicyCriticalFields
                policyId={policy.id}
                merged={merged}
                draft={draft}
                missing={missing}
                patientId={patientId}
                onChange={onChange}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
